import { forwardRef, type ElementRef } from "react";

import { cn } from "../../lib";
import { Box } from "../../primitives";
import {
  dialogDividerColor,
  DialogHeaderAlignContext,
  dialogSectionGap,
  dialogSectionPadding,
  useDialogContext,
} from "./lib";
import type { DialogHeaderProps } from "./types";

/**
 * Dialog.Header
 *
 * Sticky region above the body. Holds `Dialog.Title`, optional
 * `Dialog.Description`, optional leading icon, and (optionally) a trailing
 * close button. Its gap is 8px (12 from `md`); section padding 16px (24 from `md`).
 * `align="center"` centres the items, and the Title and Description follow it.
 * Carries a `border-b` separator unless `Dialog.Root` is given `divider={false}`.
 */
export const DialogHeader = forwardRef<ElementRef<typeof Box>, DialogHeaderProps>(
  ({ children, align = "left", className }, ref) => {
    const { divider } = useDialogContext();

    return (
      <DialogHeaderAlignContext.Provider value={align}>
        <Box
          ref={ref}
          as="header"
          className={cn(
            dialogSectionGap,
            "flex shrink-0 flex-col",
            align === "center" && "items-center",
            divider && cn("border-b", dialogDividerColor),
            dialogSectionPadding,
            className,
          )}
        >
          {children}
        </Box>
      </DialogHeaderAlignContext.Provider>
    );
  },
);

DialogHeader.displayName = "Dialog.Header";
